import { iso8601DurationToLoansTerm } from 'shared/lib';
import type { TLoansApplicationV2Data } from 'shared/types/TLoansService';
import type { TOffer } from 'shared/types/TRankingService';

import type { TCreditFormCommonFields } from 'entities/creditForm';
import { CreditPurpose } from 'entities/creditForm/lib/formatters';
import type { TPledgeAutoFields } from 'entities/pledgeAutoForm';
import { formDataToLoansCustomerPledgeAutoAdapter } from 'entities/pledgeAutoForm';
import type { TPledgeEstateFields } from 'entities/pledgeEstateForm';
import { formDataToLoansCustomerPledgeEstateAdapter } from 'entities/pledgeEstateForm';

export type TAdditionalData = {
  pledgeAutoData?: TPledgeAutoFields;
  pledgeEstateData?: TPledgeEstateFields;
  affId?: string;
  [key: string]: string | boolean | number | TPledgeAutoFields | TPledgeEstateFields | undefined | null;
};

// eslint-disable-next-line complexity
export const offerToPreApprovalDataAdapter = (
  offer: TOffer,
  formData: TCreditFormCommonFields,
  additionalData?: TAdditionalData,
): Pick<TLoansApplicationV2Data, 'customer' | 'other'> => {
  const { pledgeAutoData, pledgeEstateData, ...other } = additionalData || {};

  const isAutoPledge = !!offer.additionalInfo?.isAutoPledge;
  const isEstatePledge = !!offer.additionalInfo?.isPropertyPledge;

  const termUnit = offer.category === 'MicroCredit' ? 'day' : 'month';

  const calculatedValues =
    offer.calculatedValues && 'rate' in offer.calculatedValues ? offer.calculatedValues : undefined;
  const { perMonth, rate } = calculatedValues || {};

  const { period: _, ...creditPurpose } = new CreditPurpose(formData).map();

  const customer: TLoansApplicationV2Data['customer'] = {
    loanGracePeriod: offer.loanGracePeriod,
    loanGracePeriodUnit: offer.loanGracePeriodUnit,
    loanPerMonth: perMonth?.toString(),
    loanRate: rate?.from?.toString(),
    calcId: offer.calcId,
    productId: offer.id,
    productName: offer.productName,
    productType: offer.category!,
    organizationId: offer.bankId,
    organizationName: offer.bankName,
    loanPledge: {
      auto: isAutoPledge ? formDataToLoansCustomerPledgeAutoAdapter(pledgeAutoData) : undefined,
      estate: isEstatePledge ? formDataToLoansCustomerPledgeEstateAdapter(pledgeEstateData) : undefined,
    },
    ...creditPurpose,
    ...iso8601DurationToLoansTerm(formData.period?.value, termUnit),
  };

  return {
    customer,
    other: other as TLoansApplicationV2Data['other'],
  };
};
